import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  TextField, 
  InputAdornment, 
  Switch, 
  Button, 
  Grid, 
  Alert, 
  Stack,
  Divider,
  CircularProgress,
  Snackbar
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
// Adjust your firebase imports according to your project structure
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase_config';

const Configuration = () => {
  const [config, setConfig] = useState({
    platform_fee_percent: 0,
    min_withdrawal_amount: 0,
    chapa_fee_percent: 0,
    withdrawal_requires_approval: false,
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  // Fetch initial config from Firestore
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const docRef = doc(db, 'config', 'global_settings');
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          setConfig({
            platform_fee_percent: data.platform_fee_percent || 0,
            min_withdrawal_amount: data.min_withdrawal_amount || 0,
            chapa_fee_percent: data.chapa_fee_percent || 0,
            withdrawal_requires_approval: data.withdrawal_requires_approval || false,
          });
        } else {
          console.error("No global_settings document found!");
          setToast({ open: true, message: 'Configuration document not found.', severity: 'error' });
        }
      } catch (error) {
        console.error("Error fetching config:", error);
        setToast({ open: true, message: 'Failed to load configuration.', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setConfig(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : Number(value)
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const docRef = doc(db, 'config', 'global_settings');
      await updateDoc(docRef, {
        platform_fee_percent: config.platform_fee_percent,
        min_withdrawal_amount: config.min_withdrawal_amount,
        chapa_fee_percent: config.chapa_fee_percent,
        withdrawal_requires_approval: config.withdrawal_requires_approval,
        updatedAt: serverTimestamp(),
        updatedBy: "admin_ui" // You can replace this with the actual logged-in admin's ID
      });
      setToast({ open: true, message: 'Settings saved successfully!', severity: 'success' });
    } catch (error) {
      console.error("Error saving config:", error);
      setToast({ open: true, message: 'Failed to save settings.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Calculations for the dynamic example
  const exampleSessionAmount = 300;
  const platformShare = (exampleSessionAmount * (config.platform_fee_percent / 100)).toFixed(0);
  const teacherShare = (exampleSessionAmount - platformShare).toFixed(0);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 900, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          System Configuration
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Update platform settings and fees
        </Typography>
      </Box>

      <Stack spacing={4}>
        {/* Platform Fee Card */}
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Platform Fee
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Percentage deducted from teacher earnings per session
            </Typography>

            <Grid container spacing={4} alignItems="flex-start">
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Platform Fee Percentage
                </Typography>
                <TextField
                  fullWidth
                  name="platform_fee_percent"
                  type="number"
                  value={config.platform_fee_percent}
                  onChange={handleInputChange}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  size="small"
                />
              </Grid>

              {/* Dynamic Example Box */}
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Example
                </Typography>
                <Box sx={{ p: 2, bgcolor: '#f9f9f9', borderRadius: 2, border: '1px solid #eee' }}>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    Session: {exampleSessionAmount} ETB → Teacher gets <Typography component="span" fontWeight="bold" color="success.main">{teacherShare} ETB</Typography>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Platform receives {platformShare} ETB
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Alert severity="warning" sx={{ mt: 4, borderRadius: 2, bgcolor: '#fff4e5' }}>
              This affects all future session completions
            </Alert>
          </CardContent>
        </Card>

        {/* Withdrawal Settings Card */}
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Withdrawal Settings
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Configure withdrawal limits and fees
            </Typography>

            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Minimum Withdrawal Amount
                </Typography>
                <TextField
                  fullWidth
                  name="min_withdrawal_amount"
                  type="number"
                  value={config.min_withdrawal_amount}
                  onChange={handleInputChange}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">ETB</InputAdornment>,
                  }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Chapa Fee Percentage
                </Typography>
                <TextField
                  fullWidth
                  name="chapa_fee_percent"
                  type="number"
                  value={config.chapa_fee_percent}
                  onChange={handleInputChange}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  size="small"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="body1" fontWeight="500">
                  Require Admin Approval
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  All withdrawals must be approved by an admin
                </Typography>
              </Box>
              <Switch 
                name="withdrawal_requires_approval"
                checked={config.withdrawal_requires_approval}
                onChange={handleInputChange}
                color="primary"
              />
            </Box>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="contained" 
            onClick={handleSave}
            disabled={saving}
            startIcon={<SaveIcon />}
            sx={{ 
              bgcolor: '#008080', 
              '&:hover': { bgcolor: '#006666' },
              textTransform: 'none',
              px: 3
            }}
          >
            {saving ? 'Saving...' : 'Save All Changes'}
          </Button>
          <Button 
            variant="outlined" 
            color="inherit"
            sx={{ textTransform: 'none', px: 3 }}
          >
            Cancel
          </Button>
        </Box>
      </Stack>

      {/* Feedback Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast({ ...toast, open: false })}
        message={toast.message}
      />
    </Box>
  );
};

export default Configuration;